/// <reference lib="dom" />
import { clamp } from "@utils/utils";
import { ParamDict, ParamDictToProps } from "@utils/params";
import { G } from "../graphConstants.ts";
import { makeLinearScale, pathFromPixelPoints, sampleCurveByScale } from "../scaleUtils.ts";
import { appendBackgroundRect, appendFixedSvg, appendScaledSvg, SvgSelection } from "../d3Chart.ts";

//#region LfoSvgParams / LfoSvgOpts

export const LfoSvgParams = {
  "display/maxCycles": { type: "float", value: 4 },
  "display/rateMin": { type: "float", value: 0.01 },
  "display/rateMax": { type: "float", value: 11 },
  "editor/width": { type: "float", value: 10 * G },
  "editor/height": { type: "float", value: 4 * G },
  "editor/pad": { type: "float", value: Math.round(G / 4) },
  "inline/height": { type: "float", value: Math.round(G * 0.75) },
  "inline/pad": { type: "float", value: 1 },
  "inline/virtualWidth": { type: "float", value: 8 * G },
} satisfies ParamDict;
export type LfoSvgParams = typeof LfoSvgParams;
export const LfoSvgOptsDefaults = ParamDictToProps(LfoSvgParams);
export type LfoSvgOpts = typeof LfoSvgOptsDefaults;

//#endregion

//#region Math — pure, parameterized

// Builds a sine wave path. Rate maps to cycle count (more rate = more visible cycles).
// phaseOffsetDeg shifts the wave for stereo phase display. x comes from a shared domain scale;
// y is computed directly in pixel space (a sine offset from the center line), so this joins
// points via pathFromPixelPoints rather than curvePathFrom's two-scale mapping.
export function buildLfoPath(
  rate: number,
  depth: number,
  width: number,
  height: number,
  pad: number,
  opts: Pick<LfoSvgOpts, "display/maxCycles" | "display/rateMin" | "display/rateMax">,
  phaseOffsetDeg = 0,
  nPoints = 80,
): string {
  const rateNorm = (rate - opts["display/rateMin"]) /
    Math.max(opts["display/rateMax"] - opts["display/rateMin"], 0.0001);
  const cycles = clamp(rateNorm * opts["display/maxCycles"], 0.05, opts["display/maxCycles"]);
  const centerY = height / 2;
  const amplitude = depth * (centerY - pad);
  const phaseOffset = (phaseOffsetDeg / 180) * Math.PI;

  const xScale = makeLinearScale([0, 1], [pad, width - pad]);
  const points = sampleCurveByScale(
    (normT) => centerY - amplitude * Math.sin(normT * Math.PI * 2 * cycles + phaseOffset),
    xScale,
    nPoints,
  ).map((p) => ({ x: xScale(p.x), y: p.y }));
  return pathFromPixelPoints(points);
}

// Maps rate [rateMin, rateMax] → x position [pad, width-pad].
export function rateToX(
  rate: number,
  width: number,
  pad: number,
  opts: Pick<LfoSvgOpts, "display/rateMin" | "display/rateMax">,
): number {
  return makeLinearScale([opts["display/rateMin"], opts["display/rateMax"]], [pad, width - pad])(
    rate,
  );
}

// Maps x position → rate.
export function xToRate(
  xPos: number,
  width: number,
  pad: number,
  opts: Pick<LfoSvgOpts, "display/rateMin" | "display/rateMax">,
): number {
  return makeLinearScale([opts["display/rateMin"], opts["display/rateMax"]], [pad, width - pad])
    .invert(xPos);
}

// Maps depth [0, 1] → y position (inverted: depth=1 is near top).
export function depthToY(depth: number, height: number, pad: number): number {
  return makeLinearScale([0, 1], [height - pad, pad])(depth);
}

// Maps y position → depth [0, 1].
export function yToDepth(yPos: number, height: number, pad: number): number {
  return makeLinearScale([0, 1], [height - pad, pad]).invert(yPos);
}

//#endregion

//#region d3 shell append helpers

// Mounts the svg shell shared by every LFO-wave chart (LfoRateDepthCurve, TremoloCurve, …):
// fixed-size crosshair svg + background/center-axis grid for "editor" mode, or a bare
// scaled-to-fit svg for "inline" mode (matching mountFreqChartSvg's role for the
// frequency-response-curves/frequency-bands families). Consumers append their own wave/handle
// marks on top — this is only the background, not a whole chart.
export function mountLfoChartSvg(
  container: HTMLDivElement,
  opts: LfoSvgOpts,
  mode: "editor" | "inline",
): { svg: SvgSelection; width: number; height: number; pad: number } {
  const width = mode === "editor" ? opts["editor/width"] : opts["inline/virtualWidth"];
  const height = mode === "editor" ? opts["editor/height"] : opts["inline/height"];
  const pad = mode === "editor" ? opts["editor/pad"] : opts["inline/pad"];

  const svg = mode === "editor"
    ? appendFixedSvg(container, width, height)
    : appendScaledSvg(container, width, height);

  if (mode === "editor") appendLfoEditorGrid(svg, opts);

  return { svg, width, height, pad };
}

// Draws the LFO-editor background + center axis directly into a d3-owned svg — replaces the
// old LfoSvgEditor JSX shell. Call once during a consumer's chart init, before appending its
// own wave/handle marks.
export function appendLfoEditorGrid(svg: SvgSelection, opts: LfoSvgOpts): void {
  const svgWidth = opts["editor/width"];
  const svgHeight = opts["editor/height"];
  const pad = opts["editor/pad"];
  const centerY = svgHeight / 2;

  appendBackgroundRect(svg, svgWidth, svgHeight);

  svg.append("line")
    .attr("x1", pad)
    .attr("y1", centerY)
    .attr("x2", svgWidth - pad)
    .attr("y2", centerY)
    .attr("stroke", "#2a2a2a")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "2,2");
}

//#endregion
